<template>
  <v-container>
    <dialog-confirm ref="delete-dialog">
      {{ $t('are_you_sure_you_want_to_delete_this') }}
    </dialog-confirm>
    <dialog-confirm ref="restore-dialog">
      {{ $t('are_you_sure_you_want_to_restore_this') }}
    </dialog-confirm>
    <div v-if="modelLoaded">
      <customer-context-prompt :resource="model"/>
      <div class="d-flex justify-space-between align-center">
        <h2>
          {{ $tc('edit_location') }}
          <span class="subheader ml-3 grey--text">{{ model.label }}</span>
        </h2>
        <div v-if="user.can('delete_location')" class="mr-2">
          <v-btn v-if="!model.deleted" color="danger" outlined @click="deleteLocation" :loading="deleting">
            <v-icon>mdi-delete</v-icon>
            {{ $t('delete_location') }}
          </v-btn>
          <v-btn v-else color="warning" outlined @click="restoreLocation" :loading="restoring">
            <v-icon>mdi-arrow-u-left-top</v-icon>
            {{ $t('restore') }}
          </v-btn>
        </div>
      </div>
      <div>
        <location-form :model="model" />
        <div class="location-metadata mt-4">
          <h3>{{ $t('recent_visits') }}:</h3>
          <p>{{ $t('location_recent_visits_info') }}</p>
          <recent-visits v-if="modelLoaded" :location="model"></recent-visits>
        </div>
        <v-card v-if="user.can('view_auditlog')" no-body class="my-4">
          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-header>{{ $tc('audit_log') }}</v-expansion-panel-header>
              <v-expansion-panel-content>
                <audit-log
                  ref="audit-log"
                  autoload
                  :table-controls="['actor', 'date_range']"
                  :table-columns="['user', 'customer', 'action', 'timestamp']"
                  :initSearchCriteria="auditLogSearchCriteria"/>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card>
        <v-card class="root-only my-4" v-if="user.can('view_rootentity')" >
          <v-card-title>{{ $t('admin_only') }}</v-card-title>
          <v-card-text>
            <div v-if="user.can('assign_tenant_location')" >
              <vf-autocomplete
                :label="$tc('customer', 2)"
                v-model="currentCustomers"
                track-by="value"
                :disabled="updatingAssignedCustomers"
                :items="availableCustomers"
                return-object/>
            </div>
            <div class="my-4" v-if="user.can('assign_tenant_location')">
              <v-select
                :label="$t('primary_customer')"
                v-model="primaryCustomer"
                track-by="value"
                :disabled="updatingAssignedCustomers"
                :items="currentCustomers"
                return-object/>
            </div>
            <v-expansion-panels>
              <v-expansion-panel>
                <v-expansion-panel-header>Metadata</v-expansion-panel-header>
                <v-expansion-panel-content>
                  <h5>Location Metadata:</h5>
                  <dl class="row">
                    <template v-for="(item, key) in model.meta">
                      <dt class="col-sm-3 text-right" :key="key">{{ key }}</dt>
                      <dd class="col-sm-9" :key="item">{{ item }}</dd>
                    </template>
                  </dl>
                  <div v-if="model.geofence_type === 'polygon'">
                    <h3>Polygon Coordinates:</h3>
                    <pre>{{ model.polygon }}</pre>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
              <v-expansion-panel>
                <v-expansion-panel-header>
                  {{ $tc('note', 2) }}
                  <template v-slot:actions v-if="model.notes.length > 0">
                    <v-badge overlap :content="model.notes.length">
                      <v-icon color="primary">
                        mdi-alert-circle-outline
                      </v-icon>
                    </v-badge>
                  </template>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <resource-notes ref="locationNotes" v-model="newNoteContent" :notes="model.notes" v-on:create-note="createNote($event)" />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card-text>
        </v-card>
      </div>
    </div>
  </v-container>
</template>

<script>
import Vue from 'vue'
import CustomerContextPrompt from '@/components/shared/CustomerContextPrompt'
import LocationForm from '@/components/location/LocationForm'
import AuditLog from '@/components/audit-log/AuditLog'
import DialogConfirm from '@/components/shared/DialogConfirm'
import VfAutocomplete from "@/components/shared/VfAutocomplete.vue";
import ResourceNotes from '@/components/shared/ResourceNotes.vue'
import RecentVisits from '@/components/location/RecentVisits'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import BrowserUtilitiesMixin from '@/components/mixins/BrowserUtilitiesMixin'
import { difference, isEmpty } from 'lodash'

export default {
  name: 'LocationUpdate',
  components: {
    VfAutocomplete,
    CustomerContextPrompt,
    LocationForm,
    AuditLog,
    DialogConfirm,
    ResourceNotes,
    RecentVisits
  },
  mixins: [
    CurrentUserMixin,
    BrowserUtilitiesMixin
  ],
  data () {
    return {
      model: {},
      updatingAssignedCustomers: false,
      newNoteContent: '',
      deleting: false,
      restoring: false
    }
  },
  methods: {
    isEmpty: isEmpty,
    fetchModel () {
      this.$orm('Location')
        .find(this.$route.params.id, { params: { expand: 'customers,primary_customer,notes.user_email,recent_visits.vehicle' } })
        .then((model) => {
          this.model = model
          // Currently, model.meta from the API can be null or a blank array.
          // Make sure that model.meta is an object.
          // Make sure that model.meta.RoutePlanTZ exists, defaults to UTC and is reactive.
          if (isEmpty(this.model.meta)) {
            Vue.set(this.model, 'meta', {})
          }
          if (this.model.meta.RoutePlanTZ == null) {
            Vue.set(this.model.meta, 'RoutePlanTZ', 'UTC')
          }
        })
        .catch(error => {
          this.handleError(error)
        })
    },
    /**
     * Called when we add, remove, or change the primary customer. Fetches an
     * updated list of customer relationships and when complete shows the
     * appropriate banner
     */
    fetchAndUpdateCustomers () {
      this.model.fetchCustomersRelations()
        .then(customerRelations => {
          this.model.updateCustomers(customerRelations)

          this.$store.dispatch(
            'globalMessagingModule/showSuccessFlashBanner',
            `${this.$i18n.tc('location')} ${this.model.label} ${this.$i18n.t('generic_save_success')}`
          )

          this.updatingAssignedCustomers = false
        })
        .catch(error => {
          this.updatingAssignedCustomers = false
          this.handleError(error)
        })
    },
    /**
     * Create a note attached to this location
     */
    createNote (note) {
      const newNote = {
        resource: 'location',
        entity_id: this.model.id,
        user_id: this.user.id,
        note: note,
        context: 'frontend/form/location'
      }

      this.$orm('Note')
        .createOrUpdate(newNote, { params: { expand: 'user_email', fields: 'id,note,created,user_email' } })
        .then(note => {
          this.model.notes.push(note)
          this.$refs.locationNotes.resetNoteBody()
          this.$refs.locationNotes.newNoteSaveComplete()

          this.$store.dispatch(
            'globalMessagingModule/showSuccessFlashBanner',
            `${this.$i18n.tc('note')} ${this.$i18n.t('generic_save_success')}`
          )
        })
        .catch(error => {
          this.$refs.locationNotes.newNoteSaveComplete()
          this.handleError(error)
        })
    },
    /**
     * Delete the location
     * @return void
     */
    async deleteLocation () {
      if (await this.$refs['delete-dialog'].confirm()) {
        this.deleting = true
        this.$gtag.event('delete', { event_category: 'Location' })
        try {
          await this.model.destroy()
          this.$router.push('/locations', () => {
            this.$store.dispatch(
              'globalMessagingModule/showSuccessFlashBanner',
              `${this.$tc('location')} ${this.model.label} ${this.$t('generic_delete_success')}`
            )
          })
        } catch (error) {
          this.handleError(error)
        } finally {
          this.deleting = false
        }
      }
    },
    /**
     * Restore/undelete the location
     * @return void
     */
    async restoreLocation () {
      if (await this.$refs['restore-dialog'].confirm()) {
        this.restoring = true
        this.$gtag.event('restore', { event_category: 'Location' })
        try {
          await this.$orm('Location').update(this.model.id, { deleted: null })
          this.$router.push('/locations', () => {
            this.$store.dispatch(
              'globalMessagingModule/showSuccessFlashBanner',
              `${this.$tc('location')} ${this.model.label} ${this.$t('generic_restore_success')}`
            )
          })
        } catch (error) {
          this.handleError(error)
        } finally {
          this.restoring = false
        }
      }
    },
    /**
     * Trigger an audit log refresh
     */
    refreshAuditLog () {
      this.$refs['audit-log'].refreshResults()
    }
  },
  created () {
    this.fetchModel()
  },
  computed: {
    modelLoaded () {
      return this.model.id != null
    },
    auditLogSearchCriteria () {
      return {
        resourceType: 'location',
        primaryKey: this.model.id,
        createdAfter: this.model.created
      }
    },
    currentCustomers: {

      get: function () {
        if (this.model.customers === undefined) {
          return []
        }

        const current = []

        this.model.customers.forEach(function (customer) {
          current.push({ text: customer.name, value: customer.id })
        })

        return current
      },

      /**
       * Calculates the difference between the new set of customers and
       * what is currently on the model, makes the appropriate api call,
       * and updates the local state
       */
      set: function (updatedCustomers) {
        // Temporarily disable the input while we update the backend
        this.updatingAssignedCustomers = true

        const customersToLink = difference(updatedCustomers, this.currentCustomers)
        const customersToUnlink = difference(this.currentCustomers, updatedCustomers)

        // The nature of the form is that we only allow one update at a time
        // (and we disable the form while the update is taking place). Determine
        // what the user did (add or remove) and then process the change

        let action = ''
        let customer = {}

        if (customersToLink.length > 0) {
          action = 'linkCustomer'
          customer = customersToLink[0]
        } else {
          action = 'unlinkCustomer'
          customer = customersToUnlink[0]
        }

        this.model[action](customer.value)
          .then(response => {
            this.fetchAndUpdateCustomers()
          })
          .catch(error => {
            this.handleError(error)
            this.updatingAssignedCustomers = false
          })
      }
    },

    primaryCustomer: {

      get: function () {
        if (this.model.primary_customer == null) {
          return { text: 'Please select one', value: -1 }
        } else {
          return { text: this.model.primary_customer.name, value: this.model.primary_customer.id }
        }
      },

      /**
       * Triggers an api call and updates the local state when it returns
       * successfully
       */
      set: function (newPrimaryCustomer) {
        // Temporarily disable the input while we update the backend
        this.updatingAssignedCustomers = true

        this.model.setPrimaryCustomer(newPrimaryCustomer.value)
          .then(response => {
            this.fetchAndUpdateCustomers()
          })
          .catch(error => {
            this.updatingAssignedCustomers = false
            this.handleError(error)
          })
      }
    },

    /**
     * Lists customers available to the current user (customers in their scope)
     */
    availableCustomers () {
      if (this.customers === undefined) {
        return []
      }

      const inScopeCustomers = []
      this.customers.forEach(function (customer) {
        inScopeCustomers.push({ text: customer.name, value: customer.id })
      })

      return inScopeCustomers
    }
  },
  watch: {
    model: function (newModel, oldModel) {
      this.updateBrowserTitle(this.$tc('location') + ': ' + newModel.label)
    }
  }
}
</script>
